import React from "react";
import { Link, useLocation } from "react-router-dom";
import "./TeamNavigation.css";

const TeamNavigation = ({ currentUser, teamId, userMembership }) => {
  const location = useLocation();

  const isCoachOrManager =
    userMembership && ["coach", "manager"].includes(userMembership.role);

  const navItems = [
    {
      path: "/dashboard",
      icon: "🏠",
      label: "Back to Dashboard",
      description: "Return to main dashboard",
      isExternal: true,
    },
    {
      path: "/teams",
      icon: "🔍",
      label: "Browse Teams",
      description: "Discover new teams",
    },
    {
      path: "/teams/my-teams",
      icon: "👥",
      label: "My Teams",
      description: "Your team memberships",
      requiresAuth: true,
    },
    {
      path: "/teams/create",
      icon: "➕",
      label: "Create Team",
      description: "Start a new team",
      requiresAuth: true,
    },
  ];

  const teamNavItems = teamId
    ? [
        {
          path: `/teams/${teamId}`,
          icon: "📋",
          label: "Team Details",
          description: "Team information",
        },
      ]
    : [];

  const isActiveRoute = (path) => {
    if (path === "/teams" && location.pathname === "/teams") return true;
    if (path !== "/teams" && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <nav className="team-navigation">
      <div className="nav-container">
        {/* Main Navigation */}
        <div className="nav-section">
          <h3 className="nav-section-title">
            <span className="icon">🏆</span>
            Teams
          </h3>
          <ul className="nav-list">
            {navItems.map((item) => {
              // Hide auth-required items for non-authenticated users
              if (item.requiresAuth && !currentUser) return null;

              return (
                <li key={item.path} className="nav-item">
                  {item.isExternal ? (
                    <a
                      href={item.path}
                      className={`nav-link ${isActiveRoute(item.path) ? "active" : ""}`}
                    >
                      <span className="nav-icon">{item.icon}</span>
                      <div className="nav-content">
                        <span className="nav-label">{item.label}</span>
                        <span className="nav-description">
                          {item.description}
                        </span>
                      </div>
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      className={`nav-link ${isActiveRoute(item.path) ? "active" : ""}`}
                    >
                      <span className="nav-icon">{item.icon}</span>
                      <div className="nav-content">
                        <span className="nav-label">{item.label}</span>
                        <span className="nav-description">
                          {item.description}
                        </span>
                      </div>
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Team-Specific Navigation */}
        {teamId && userMembership && (
          <div className="nav-section">
            <h3 className="nav-section-title">
              <span className="icon">⚙️</span>
              Team Actions
            </h3>
            <ul className="nav-list">
              {teamNavItems.map((item) => {
                // Hide membership-required items for non-members
                if (item.requiresMembership && !userMembership) return null;

                // Hide coach-only items for non-coaches
                if (item.requiresCoach && !isCoachOrManager) return null;

                return (
                  <li key={item.path} className="nav-item">
                    <Link
                      to={item.path}
                      className={`nav-link ${isActiveRoute(item.path) ? "active" : ""}`}
                    >
                      <span className="nav-icon">{item.icon}</span>
                      <div className="nav-content">
                        <span className="nav-label">{item.label}</span>
                        <span className="nav-description">
                          {item.description}
                        </span>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Quick Actions */}
        {currentUser && (
          <div className="nav-section">
            <h3 className="nav-section-title">
              <span className="icon">⚡</span>
              Quick Actions
            </h3>
            <div className="quick-actions">
              <Link to="/teams/create" className="quick-action-btn">
                <span className="icon">➕</span>
                New Team
              </Link>
            </div>
          </div>
        )}

        {/* User Status */}
        {currentUser && (
          <div className="nav-section user-status">
            <div className="user-info">
              <div className="user-avatar">
                {currentUser.profile_picture ? (
                  <img
                    src={currentUser.profile_picture}
                    alt={currentUser.username}
                  />
                ) : (
                  <span className="default-avatar">👤</span>
                )}
              </div>
              <div className="user-details">
                <span className="user-name">
                  {currentUser.first_name} {currentUser.last_name}
                </span>
                <span className="user-role">@{currentUser.username}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default TeamNavigation;
